import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';

import axios from 'axios';
import {
  getAuth,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  User,
  UserCredential
} from 'firebase/auth';

import { Spinner } from '@/components/generic';
import {
  createAuthProvider,
  getSsoProviderById,
  isEntraSsoFirebaseProvider,
  isSsoAuthUser,
  resolveEffectiveSignInProvider
} from '@/configuration/ssoProviders';
import { queryClient } from '@/configuration/react-query';
import { ProfilePictureProvider } from '@/contexts';
import { ORGANIZATION_PERMISSION_TYPE, PERMISSION_TYPE } from '@/enums';
import {
  AUTH_ERROR_MESSAGE_KEYS,
  capturePendingEntraLink,
  capturePendingSsoLink,
  capturePendingSsoLinkFromResult,
  clearPendingSsoLink,
  ensureEntraAssignmentBeforeTokenRefresh,
  getEntraAccessRevokeMessageKey,
  getEntraRevalidationDelayMs,
  getOAuthAccessTokenFromError,
  getPendingSsoLinkEmail,
  getSsoIdTokenFromError,
  isEntraAccessRevokedOutcome,
  isMissingAppUserError,
  isSsoPopupCancelledError,
  linkPendingSsoCredential,
  onEntraAccessRevoked,
  resetEntraRevalidationState,
  revalidateEntraAssignmentAtTokenBoundary,
  signInWithAuthPopup,
  signInWithEntraPopup
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import i18n from '@/i18n';
import { useGetCurrentUserQuery } from '@/services';
import { registrationEndpoints } from '@/services/registration.service';
import { useFilterStore } from '@/store';
import { ChildrenProps, UserData } from '@/types';
import { SsoProviderConfig } from '@/types/ssoProvider';

const USER_DATA_OFFLINE_KEY = 'sentoa_user_offline_cache';

type SsoLoginOptions = {
  inviteToken?: string;
};

type AuthContextType = {
  user: User | null | undefined;
  login: (email: string, pass: string) => Promise<UserCredential> | undefined;
  loginWithSsoProvider: (providerId: string, options?: SsoLoginOptions) => Promise<void>;
  completePendingSsoLink: (signedInUser: User) => Promise<void>;
  cancelPendingSsoLink: () => void;
  pendingSsoLinkEmail: string | null;
  signInProvider: string | null;
  logout: () => Promise<void>;
  currentUserData: UserData | null;
  permissions: string[];
  organizationPermissions: string[];
  isAuthLoading: boolean;
  isCurrentUserLoading: boolean;
  isReady: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: PERMISSION_TYPE) => boolean;
  hasOrganizationPermission: (permission: ORGANIZATION_PERMISSION_TYPE) => boolean;
  hasAllPermissions: (permissions: PERMISSION_TYPE[]) => boolean;
  hasAllOrganizationPermissions: (permissions: ORGANIZATION_PERMISSION_TYPE[]) => boolean;
};

const initialContext: AuthContextType = {
  user: undefined,
  currentUserData: null,
  login: () => undefined,
  loginWithSsoProvider: () => Promise.resolve(),
  completePendingSsoLink: () => Promise.resolve(),
  cancelPendingSsoLink: () => void 0,
  pendingSsoLinkEmail: null,
  signInProvider: null,
  logout: () => Promise.resolve(),
  permissions: [],
  organizationPermissions: [],
  isAuthenticated: false,
  isAuthLoading: true,
  isCurrentUserLoading: false,
  isReady: false,
  hasPermission: () => false,
  hasOrganizationPermission: () => false,
  hasAllPermissions: () => false,
  hasAllOrganizationPermissions: () => false
};

export const AuthContext = createContext(initialContext);

type AuthProviderProps = ChildrenProps;

const resolveSsoSignInError = async (
  auth: ReturnType<typeof getAuth>,
  error: unknown,
  setPendingEmail: (email: string | null) => void,
  providerId: string,
  inviteToken?: string
): Promise<void> => {
  const keepPendingLinkAndRethrow = (): never => {
    if (capturePendingSsoLink(error, providerId)) {
      setPendingEmail(getPendingSsoLinkEmail());
    }
    throw error;
  };

  const oauthIdToken = getSsoIdTokenFromError(error);
  const oauthAccessToken = getOAuthAccessTokenFromError(error);
  if (oauthIdToken || oauthAccessToken) {
    try {
      const { data } = await registrationEndpoints.completeSsoInvite({
        idToken: oauthIdToken || undefined,
        accessToken: oauthAccessToken || undefined,
        providerId,
        inviteToken
      });

      if (data.requiresPasswordLink || data.code === 'PASSWORD_LINK_REQUIRED') {
        keepPendingLinkAndRethrow();
      }

      if (data.customToken) {
        await signInWithCustomToken(auth, data.customToken);
        clearPendingSsoLink();
        return;
      }
    } catch (completeError) {
      if (completeError === error) {
        throw error;
      }
      throw completeError;
    }
  }

  keepPendingLinkAndRethrow();
};

const PASSWORD_LINK_REQUIRED_CODE = 'auth/account-exists-with-different-credential';

/**
 * Microsoft sign-in starts at Entra. Firebase is contacted only after the API
 * has linked the existing Sentoa user, so a rejected login creates no Firebase account.
 */
const signInWithEntraProvider = async (
  auth: ReturnType<typeof getAuth>,
  providerConfig: SsoProviderConfig,
  inviteToken: string | undefined,
  setPendingEmail: (email: string | null) => void
): Promise<void> => {
  if (!providerConfig.clientId || !providerConfig.tenant) {
    throw new Error('Microsoft sign-in is not configured.');
  }

  const entra = await signInWithEntraPopup(providerConfig);
  const { data } = await registrationEndpoints.acceptEntraSignIn({
    idToken: entra.idToken,
    providerId: providerConfig.firebaseProviderId,
    inviteToken
  });

  if (data.requiresPasswordLink || data.code === 'PASSWORD_LINK_REQUIRED') {
    capturePendingEntraLink(
      entra.idToken,
      data.email ?? entra.email,
      providerConfig.firebaseProviderId
    );
    setPendingEmail(getPendingSsoLinkEmail());
    throw Object.assign(new Error('Password link required'), {
      code: PASSWORD_LINK_REQUIRED_CODE
    });
  }

  if (!data.customToken) {
    throw new Error('Microsoft sign-in could not start a session.');
  }

  await signInWithCustomToken(auth, data.customToken);
  clearPendingSsoLink();
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const auth = getAuth();
  const [user, loading] = useAuthState(auth);
  const { setOrganizationFilter } = useFilterStore();
  const isOnline = useOnlineStatus();
  const [pendingSsoLinkEmail, setPendingSsoLinkEmail] = useState<string | null>(null);
  const [signInProvider, setSignInProvider] = useState<string | null>(null);
  const [isSsoSignInValidating, setIsSsoSignInValidating] = useState(false);

  useEffect(() => {
    if (!user) {
      setSignInProvider(null);
      return;
    }

    let cancelled = false;
    void user.getIdTokenResult().then((tokenResult) => {
      if (!cancelled) {
        setSignInProvider(resolveEffectiveSignInProvider(tokenResult.signInProvider, user));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const {
    data: currentUserData,
    isLoading: isLoadingCurrentUser,
    isError: isCurrentUserError,
    error: currentUserError
  } = useGetCurrentUserQuery(!!user && isOnline && !isSsoSignInValidating);

  const userData = currentUserData?.data ?? null;

  useEffect(() => {
    if (userData && isOnline) {
      try {
        localStorage.setItem(USER_DATA_OFFLINE_KEY, JSON.stringify(userData));
      } catch {
        // localStorage quota exceeded — ignore
      }
    }
  }, [userData, isOnline]);

  const offlineCachedUser: UserData | null = useMemo(() => {
    if (!userData) {
      try {
        const raw = localStorage.getItem(USER_DATA_OFFLINE_KEY);
        return raw ? (JSON.parse(raw) as UserData) : null;
      } catch {
        return null;
      }
    }
    return null;
  }, [userData]);

  const hasOfflineBootstrap = !isOnline && !!offlineCachedUser;

  const shouldRejectSsoSession =
    !!user &&
    isOnline &&
    !isLoadingCurrentUser &&
    isCurrentUserError &&
    isSsoAuthUser(user) &&
    isMissingAppUserError(currentUserError);

  const isReady =
    hasOfflineBootstrap ||
    (!loading && (!user || !isLoadingCurrentUser || !isOnline) && !shouldRejectSsoSession);

  const effectiveUserData: UserData | null = userData ?? offlineCachedUser;
  // TODO: Use methods hasPermission or hasAllPermissions
  const permissions = useMemo(
    () => effectiveUserData?.role?.permissions ?? [],
    [effectiveUserData]
  );
  const hasOrganizations = (effectiveUserData?.organizations?.length ?? 0) > 0;
  // TODO: Use methods hasOrganizationPermission or hasAllOrganizationPermissions
  const organizationPermissions = useMemo(
    () => effectiveUserData?.organizationPermissions ?? [],
    [effectiveUserData]
  );
  // A user without an organization is not restricted by a payment plan.
  const availableOrganizationPermissions = hasOrganizations
    ? organizationPermissions
    : Object.values(ORGANIZATION_PERMISSION_TYPE);

  const isAuthenticated = !!user && !isSsoSignInValidating;

  // TODO: Use the following methods instead of permissions and organizationPermissons arrays
  const hasPermission = useCallback(
    (permission: PERMISSION_TYPE) => permissions.includes(permission),
    [permissions]
  );
  const hasOrganizationPermission = useCallback(
    (permission: ORGANIZATION_PERMISSION_TYPE) =>
      availableOrganizationPermissions.includes(permission),
    [availableOrganizationPermissions]
  );
  const hasAllPermissions = useCallback(
    (requiredPermissions: PERMISSION_TYPE[]) =>
      requiredPermissions.every((requiredPermission) => permissions.includes(requiredPermission)),
    [permissions]
  );
  const hasAllOrganizationPermissions = useCallback(
    (requiredPermissions: ORGANIZATION_PERMISSION_TYPE[]) =>
      requiredPermissions.every((requiredPermission) =>
        availableOrganizationPermissions.includes(requiredPermission)
      ),
    [availableOrganizationPermissions]
  );

  // TODO: Move to axios.ts
  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use(
      async (config) => {
        if (config?.headers) {
          const currentUser = getAuth().currentUser;
          if (currentUser) {
            const entraOutcome = await ensureEntraAssignmentBeforeTokenRefresh(currentUser);
            if (isEntraAccessRevokedOutcome(entraOutcome)) {
              throw new Error('Entra access revoked');
            }
          }

          const token = await getAuth().currentUser?.getIdToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (err) => Promise.reject(err)
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
    };
  }, []);

  useEffect(() => {
    if (
      effectiveUserData?.currentLanguage !== null &&
      effectiveUserData?.currentLanguage !== undefined
    ) {
      void i18n.changeLanguage(String(effectiveUserData.currentLanguage));
    }
  }, [effectiveUserData]);

  const login = useCallback(
    (email: string, password: string) => signInWithEmailAndPassword(auth, email, password),
    [auth]
  );

  const loginWithSsoProvider = useCallback(async (providerId: string, options?: SsoLoginOptions) => {
    const providerConfig = getSsoProviderById(providerId);
    if (!providerConfig) {
      return;
    }

    const firebaseProviderId = providerConfig.firebaseProviderId;
    setIsSsoSignInValidating(true);
    try {
      if (isEntraSsoFirebaseProvider(firebaseProviderId)) {
        await signInWithEntraProvider(
          auth,
          providerConfig,
          options?.inviteToken,
          setPendingSsoLinkEmail
        );
        return;
      }

      try {
        // Invited Firebase users stay disabled until activation. Enable them before the popup
        // so Microsoft/Google can sign into the same email without auth/user-disabled.
        if (options?.inviteToken) {
          await registrationEndpoints.prepareSsoInvite(options.inviteToken);
        }

        const result = await signInWithAuthPopup(auth, createAuthProvider(providerConfig));
        const firebaseIdToken = await result.user.getIdToken();

        if (options?.inviteToken) {
          const { data } = await registrationEndpoints.completeSsoInvite({
            idToken: firebaseIdToken,
            firebaseUid: result.user.uid,
            providerId: firebaseProviderId,
            inviteToken: options.inviteToken
          });

          if (data.requiresPasswordLink || data.code === 'PASSWORD_LINK_REQUIRED') {
            capturePendingSsoLinkFromResult(result, firebaseProviderId, result.user.email);
            setPendingSsoLinkEmail(getPendingSsoLinkEmail());
            await auth.signOut();
            return;
          }

          if (data.customToken) {
            await signInWithCustomToken(auth, data.customToken);
            clearPendingSsoLink();
          }

          return;
        }

        await registrationEndpoints.validateSsoSignIn({
          idToken: firebaseIdToken,
          firebaseUid: result.user.uid,
          providerId: firebaseProviderId
        });
      } catch (error) {
        // Closing the popup must not run invite/link recovery — just surface cancel to the UI.
        if (isSsoPopupCancelledError(error)) {
          await auth.signOut();
          throw error;
        }

        await auth.signOut();
        if (isMissingAppUserError(error)) {
          throw error;
        }
        await resolveSsoSignInError(
          auth,
          error,
          setPendingSsoLinkEmail,
          firebaseProviderId,
          options?.inviteToken
        );
      }
    } finally {
      setIsSsoSignInValidating(false);
    }
  }, [auth]);

  const completePendingSsoLink = useCallback(async (signedInUser: User) => {
    try {
      const didLink = await linkPendingSsoCredential(signedInUser);
      setPendingSsoLinkEmail(null);
      if (didLink) {
        showToast(
          ToastType.SUCCESS,
          i18n.t(AUTH_ERROR_MESSAGE_KEYS.SSO_ACCOUNT_LINKED, { ns: 'common' })
        );
      }
    } catch {
      setPendingSsoLinkEmail(null);
      showToast(
        ToastType.ERROR,
        i18n.t(AUTH_ERROR_MESSAGE_KEYS.SSO_ACCOUNT_LINK_FAILED, { ns: 'common' })
      );
    }
  }, []);

  const cancelPendingSsoLink = useCallback(() => {
    clearPendingSsoLink();
    setPendingSsoLinkEmail(null);
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }
    completePendingSsoLink(user);
  }, [user]);

  const logout = useCallback(async () => {
    setOrganizationFilter({ value: null, label: '' });
    localStorage.clear();
    resetEntraRevalidationState();
    await queryClient.cancelQueries();
    await auth.signOut();
    queryClient.clear();
  }, [auth, setOrganizationFilter]);

  useEffect(() => {
    return onEntraAccessRevoked((reason) => {
      showToast(ToastType.ERROR, i18n.t(getEntraAccessRevokeMessageKey(reason), { ns: 'common' }));
      void logout();
    });
  }, [logout]);

  // When the Firebase ID token enters the refresh window, re-check Entra access
  // (covers idle tabs that are not hitting the axios interceptor).
  useEffect(() => {
    if (
      !user ||
      !isOnline ||
      isSsoSignInValidating ||
      !isEntraSsoFirebaseProvider(signInProvider)
    ) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleRevalidation = async (): Promise<void> => {
      const delayMs = await getEntraRevalidationDelayMs(user);
      if (cancelled) {
        return;
      }

      timeoutId = setTimeout(() => {
        void (async () => {
          const currentUser = getAuth().currentUser;
          if (cancelled || !currentUser) {
            return;
          }

          const outcome = await revalidateEntraAssignmentAtTokenBoundary(
            currentUser,
            signInProvider
          );
          if (cancelled || isEntraAccessRevokedOutcome(outcome)) {
            // onEntraAccessRevoked listener handles toast + logout
            return;
          }

          if (outcome !== 'ok') {
            // No Entra SSO cookie / popup blocked — allow Firebase refresh to continue.
            await currentUser.getIdToken();
          }

          if (!cancelled) {
            void scheduleRevalidation();
          }
        })();
      }, delayMs);
    };

    void scheduleRevalidation();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, signInProvider, isOnline, isSsoSignInValidating]);

  useEffect(() => {
    if (!shouldRejectSsoSession) {
      return;
    }

    showToast(
      ToastType.ERROR,
      i18n.t(AUTH_ERROR_MESSAGE_KEYS.NO_APP_ACCOUNT_FOR_SSO_USER, { ns: 'common' })
    );
    logout();
  }, [logout, shouldRejectSsoSession]);

  const contextValue = useMemo(
    () => ({
      user,
      login,
      loginWithSsoProvider,
      completePendingSsoLink,
      cancelPendingSsoLink,
      pendingSsoLinkEmail,
      signInProvider,
      logout,
      currentUserData: effectiveUserData,
      permissions,
      organizationPermissions: availableOrganizationPermissions,
      isAuthLoading: loading,
      isCurrentUserLoading: isLoadingCurrentUser,
      isReady,
      isAuthenticated,
      hasPermission,
      hasOrganizationPermission,
      hasAllPermissions,
      hasAllOrganizationPermissions
    }),
    [
      availableOrganizationPermissions,
      effectiveUserData,
      hasAllOrganizationPermissions,
      hasAllPermissions,
      hasOrganizationPermission,
      hasPermission,
      isAuthenticated,
      isLoadingCurrentUser,
      isReady,
      login,
      loginWithSsoProvider,
      completePendingSsoLink,
      cancelPendingSsoLink,
      pendingSsoLinkEmail,
      signInProvider,
      loading,
      logout,
      permissions,
      user
    ]
  );

  if (!isReady) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Spinner />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {((!loading && !user) ||
        !!effectiveUserData ||
        (!isOnline && isReady) ||
        isSsoSignInValidating) && (
        <ProfilePictureProvider>{children}</ProfilePictureProvider>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
